import React, { useState, useEffect, useCallback } from "react";
import { Loading, Text, Button as NextButton, Radio, Input } from '@nextui-org/react';
import { useSelector } from "react-redux";

import { mstore } from "../../store";
import endpoint from "../../config/endpoint.json";
import { secureFetch } from "../../lib/api";
import { error, success } from "../../lib/userAlerts";
import { updateTitle } from "../../lib/utils";

// Component for the auto-link toggle setting
const AutoLinkToggle = ({ isEnabled, onChange }) => (
  <div className="flex flex-wrap mb4">
    <Radio.Group
      size="sm"
      row
      css={{ mt: '0.75rem', w: '100%', justifyContent: 'space-between' }}
      label="activate"
      value={isEnabled ? 1 : 0}
      onChange={(val) => onChange(val === 1)}
    >
      <Radio
        css={{ w: '48%' }}
        color="primary"
        value={1}
        className="p-4 border rounded-lg hover:bg-gray-50"
      >
        <div className="font-medium">Enable Auto-Link</div>
        <Radio.Desc className="text-gray-600">
          Automatically link shipments based on matching criteria
        </Radio.Desc>
      </Radio>
      <Radio
        color="primary"
        css={{ w: '48%' }}
        value={0}
        className="p-4 border rounded-lg hover:bg-gray-50"
      >
        <div className="font-medium">Disable Auto-Link</div>
        <Radio.Desc className="text-gray-600">
          Manually link shipments as needed
        </Radio.Desc>
      </Radio>
    </Radio.Group>
  </div>
);

// Component for the number of tries setting
const NumberOfTriesInput = ({ value, isDisabled, onChange }) => (
  <div className="flex flex-wrap mb4">
    <div className="f5 fw2 w-70 pt-4">
      Number of reminder messages (for tracking)
    </div>
    <Input
      type='number'
      underlined
      css={{w: '100px'}}
      min={0}
      max={10}
      defaultValue={1}
      disabled={isDisabled}
      value={value}
      onChange={onChange}
    />
  </div>
);

// Component for the interval setting
const IntervalInput = ({ value, isDisabled, onChange }) => (
  <div className="flex flex-wrap mb4">
    <div className="f5 fw2 w-70 pt-4">
      Interval between reminder messages (in hours)
    </div>
    <Input
      type='number'
      underlined
      css={{w: '100px'}}
      min={1}
      max={23}
      defaultValue={1}
      disabled={isDisabled}
      value={value}
      onChange={onChange}
    />
  </div>
);

// Main component
const AutoLinkSettings = () => {
  // Get client ID from Redux
  const clientId = useSelector(state => 
    state.client?.id || mstore.getPath(["user", "company_id"])
  );

  // State
  const [settings, setSettings] = useState({
    auto_link_free_users: false,
    auto_link_max_trys: 0,
    auto_link_try_delay_hrs: 1
  });
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Fetch auto-link settings
  const getAutoLinkSettings = useCallback(async () => {
    setLoading(true);
    
    try {
      const [ok, , json] = await secureFetch(endpoint.fetchAutoLinkSettings, {});
      
      if (ok) {
        setSettings(json.autoLinkSettings);
      } else {
        error("Oops! error occurred when fetching your auto link settings.");
      }
    } catch (err) {
      error("Failed to fetch auto link settings");
    } finally {
      setLoading(false);
    }
  }, []);

  // Update auto-link settings
  const updateAutoLink = useCallback(async () => {
    setUpdating(true);
    
    try {
      const [ok] = await secureFetch(endpoint.updateAutoLinkSettings, {
        ...settings
      });
      
      if (ok) {
        success('Updated the auto link settings.');
      } else {
        error("Oops! error occurred when updating your auto link settings.");
      }
    } catch (err) {
      error("Failed to update auto link settings");
    } finally {
      setUpdating(false);
    }
  }, [settings]);

  // Handle toggle change
  const handleToggleChange = useCallback(() => {
    setSettings(prev => ({
      ...prev,
      auto_link_free_users: prev.auto_link_free_users === 1 ? 0 : 1
    }));
  }, []);

  // Handle number of tries change
  const handleTriesChange = useCallback((e) => {
    const value = e.target.value;
    setSettings(prev => ({
      ...prev,
      auto_link_max_trys: value
    }));
  }, []);

  // Handle interval change
  const handleIntervalChange = useCallback((e) => {
    const value = e.target.value;
    setSettings(prev => ({
      ...prev,
      auto_link_try_delay_hrs: value
    }));
  }, []);

  // Initial data loading
  useEffect(() => {
    updateTitle("Autolink Settings");
    getAutoLinkSettings();
  }, [clientId, getAutoLinkSettings]);

  // Render loading state
  if (loading) {
    return (
      <div className="w-100 flex flex-wrap justify-between mt3">
        <div className="ml2 pa2 w-100 mt6 flex justify-center">
          <Loading size="lg">Fetching your auto-link settings..</Loading>
        </div>
      </div>
    );
  }

  // Render settings form
  return (
    <div className="w-100 flex flex-wrap justify-between my-6">
      <div className="ml2 p-6 w-1/2 border-2 border-dotted rounded-xl shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Auto-Link Settings</h2>
          <div className="text-sm text-gray-500">
            Configure how shipments are automatically linked
          </div>
        </div>

        <AutoLinkToggle 
          isEnabled={settings.auto_link_free_users} 
          onChange={handleToggleChange} 
        />
        
        <NumberOfTriesInput 
          value={settings.auto_link_max_trys} 
          isDisabled={settings.auto_link_free_users !== 1} 
          onChange={handleTriesChange} 
        />
        
        <IntervalInput 
          value={settings.auto_link_try_delay_hrs} 
          isDisabled={settings.auto_link_free_users !== 1} 
          onChange={handleIntervalChange} 
        />

        <div className="flex justify-end pt-4">
          <NextButton
            auto
            color="primary"
            className="hover:bg-blue-600 transition-colors duration-200"
            disabled={updating}
            onClick={updateAutoLink}
          >
            {updating ? (
              <div className="flex items-center space-x-2">
                <Loading size="sm" color="white" />
                <Text color="white">Updating...</Text>
              </div>
            ) : (
              <Text color="white">Save Settings</Text>
            )}
          </NextButton>
        </div>
      </div>
    </div>
  );
};

export default AutoLinkSettings;
